import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { RefTypeItem } from '@sd-portal/shared/ref-type-item';
import { Observable, of } from 'rxjs';
import { share, tap, map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ReferenceService {
  // TODO: place holder until caching methodology put into place
  private readonly refTypeItemCache: Map<string, RefTypeItem[]> = new Map<string, RefTypeItem[]>();
  sharedRefTypeItemsRequests$: { [key: string]: Observable<RefTypeItem[]> } = {};

  constructor(private readonly http: HttpClient) {}

  getReferenceItems(
    refType: string,
    activeOnly: boolean = true,
    includeSystem: boolean = false
  ): Observable<RefTypeItem[]> {
    const cacheKey = `${refType}_${activeOnly}`;
    const refTyteItemsFromCache = this.getRefTypeItemsFromCache(cacheKey);
    if (refTyteItemsFromCache.length) {
      return of(refTyteItemsFromCache);
    }

    // share multiple incoming requests for the same type (ex trip load w/
    // multiple legs)
    if (!this.sharedRefTypeItemsRequests$[cacheKey]) {
      const url = this.getApiUrl(refType, activeOnly, includeSystem);
      this.sharedRefTypeItemsRequests$[cacheKey] = this.http.get<RefTypeItem[]>(url).pipe(
        tap(refTypeItems => {
          this.addRefTypeItemsToCache(cacheKey, refTypeItems);
        }),
        share()
      );
    }
    return this.sharedRefTypeItemsRequests$[cacheKey];
  }

  //added method to avoid dual api round trips when loading items with the only difference being the active flag
  getReferenceItemsWithActiveFlag(
    refType: string,
    activeOnly: boolean = true,
    includeSystem: boolean = false
  ): Observable<RefTypeItem[]> {
    const cacheKey = `${refType}_${activeOnly}`;
    const refTypeItemsFromCache = this.getRefTypeItemsFromCache(cacheKey);
    if (refTypeItemsFromCache.length) {
      return of(refTypeItemsFromCache);
    }

    // share multiple incoming requests for the same type (ex trip load w/
    // multiple legs)
    if (!this.sharedRefTypeItemsRequests$[cacheKey]) {
      const allCacheKey = `${refType}_${false}`;
      const activeOnlyCacheKey = `${refType}_${true}`;

      const url = this.getApiUrl(refType, false, includeSystem);
      const allResults = this.http.get<RefTypeItem[]>(url).pipe(
        tap(refTypeItems => {
          this.addRefTypeItemsToCache(allCacheKey, refTypeItems);

          const activeOnlyItems = refTypeItems.filter(item => item.active === true);
          this.addRefTypeItemsToCache(activeOnlyCacheKey, activeOnlyItems);
        })
      );

      this.sharedRefTypeItemsRequests$[allCacheKey] = allResults.pipe(share());
      this.sharedRefTypeItemsRequests$[activeOnlyCacheKey] = allResults.pipe(
        map(items => {
          return items.filter(item => item.active === true);
        }),
        share()
      );
    }
    return this.sharedRefTypeItemsRequests$[cacheKey];
  }

  getReferenceItemsNoCache(
    refType: string,
    activeOnly: boolean = true,
    includeSystem: boolean = false
  ): Observable<RefTypeItem[]> {
    return this.http.get<RefTypeItem[]>(this.getApiUrl(refType, activeOnly, includeSystem));
  }

  getById(refType: string, id: number): Observable<RefTypeItem[]> {
    const url = `${this.getApiUrl(refType)}/${id.toString()}`;
    return this.http.get<RefTypeItem[]>(url);
  }

  private getApiUrl(
    refType: string,
    activeOnly: boolean = true,
    includeSystem: boolean = false
  ): string {
    return `/sdapi/profile/Reference/${refType}?activeOnly=${activeOnly}&includeSystem=${includeSystem}`;
  }

  private getRefTypeItemsFromCache(cacheKey: string): RefTypeItem[] {
    return this.refTypeItemCache.get(cacheKey) || [];
  }

  private addRefTypeItemsToCache(cacheKey: string, refTypeItems: RefTypeItem[]): void {
    if (!this.refTypeItemCache.has(cacheKey)) {
      this.refTypeItemCache.set(cacheKey, refTypeItems);
    }
  }
}
